import { useTranslation } from '@/src/i18n';
import { User } from '@/src/domain/models';
import { ActivityIndicator, FlatList, Modal, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from '../TournamentDetailScreen.styles';

interface AddParticipantModalProps {
  visible: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searching: boolean;
  searchResults: User[];
  selectedUser: User | null;
  onSelectUser: (user: User | null) => void;
  onAdd: () => void;
  onCancel: () => void;
  colors: {
    overlay: string;
    card: string;
    surface: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
  };
}

export const AddParticipantModal = ({
  visible,
  searchQuery,
  setSearchQuery,
  searching,
  searchResults,
  selectedUser,
  onSelectUser,
  onAdd,
  onCancel,
  colors,
}: AddParticipantModalProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  return (
    <Modal visible={visible} animationType="slide" transparent={true} onRequestClose={onCancel}>
      <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
        <View style={[styles.addAdminModal, { backgroundColor: colors.card }]}>
          <Text style={[styles.addAdminModalTitle, { color: colors.text }]}>{t('tournaments.addRegisteredUser')}</Text>

          <Text style={[styles.addAdminModalLabel, { color: colors.textSecondary }]}>
            {t('tournaments.searchByNameOrEmail')}
          </Text>

          {selectedUser ? (
            <TouchableOpacity
              style={[styles.participantSearchSelected, { backgroundColor: colors.surface, borderColor: colors.primary }]}
              onPress={() => onSelectUser(null)}
            >
              <View style={{ flex: 1 }}>
                <Text style={[{ color: colors.text, fontWeight: '600', fontSize: 15 }]}>
                  {selectedUser.name || selectedUser.email}
                </Text>
                {selectedUser.name && (
                  <Text style={[{ color: colors.textSecondary, fontSize: 13 }]}>{selectedUser.email}</Text>
                )}
              </View>
              <Text style={[{ color: colors.textSecondary, fontSize: 20, paddingLeft: 8 }]}>✕</Text>
            </TouchableOpacity>
          ) : (
            <>
              <TextInput
                style={[
                  styles.addAdminInput,
                  { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text },
                ]}
                placeholder={t('tournaments.searchNameOrEmailPlaceholder')}
                placeholderTextColor={colors.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCapitalize="none"
                autoCorrect={false}
              />

              {searching && (
                <ActivityIndicator color={colors.primary} style={{ marginBottom: 10 }} />
              )}

              {!searching && searchResults.length > 0 && (
                <FlatList
                  data={searchResults}
                  keyExtractor={(item) => item.id}
                  style={[styles.participantSearchResults, { borderColor: colors.border }]}
                  keyboardShouldPersistTaps="handled"
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={[styles.participantSearchResultItem, { borderBottomColor: colors.border }]}
                      onPress={() => {
                        onSelectUser(item);
                        setSearchQuery('');
                      }}
                    >
                      <Text style={[{ color: colors.text, fontSize: 15, fontWeight: '500' }]}>
                        {item.name || item.email}
                      </Text>
                      {item.name && (
                        <Text style={[{ color: colors.textSecondary, fontSize: 13 }]}>{item.email}</Text>
                      )}
                    </TouchableOpacity>
                  )}
                />
              )}

              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <Text style={[{ color: colors.textSecondary, fontSize: 14, marginBottom: 10, textAlign: 'center' }]}>
                  {t('alerts.userNotFound')}
                </Text>
              )}
            </>
          )}

          <TouchableOpacity
            style={[styles.addAdminModalBtn, { backgroundColor: (!selectedUser || searching) ? colors.border : colors.primary }]}
            onPress={onAdd}
            disabled={!selectedUser || searching}
          >
            {searching ? (
              <ActivityIndicator color={colors.primaryText} />
            ) : (
              <Text style={[styles.addAdminModalBtnText, { color: colors.primaryText }]}>{t('tournaments.addParticipant')}</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity style={styles.addAdminModalCancelBtn} onPress={onCancel}>
            <Text style={[styles.addAdminModalCancelBtnText, { color: colors.primary }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};
